import { useEffect, useState } from "react";
import { searchOrgInbox, searchUserInbox } from "../../api/api";

const DEBOUNCE_MS = 250;

/**
 * Debounced inbox search against chats (by name) and messages (content/author).
 * @param {{
 *   userId?: number,
 *   orgId?: number | null,
 *   query: string,
 *   enabled?: boolean,
 * }} options
 * @returns {{
 *   chats: Array<object>,
 *   messages: Array<object>,
 *   loading: boolean,
 *   error: string,
 * }}
 */
export function useInboxSearch({ userId, orgId = null, query, enabled = true }) {
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [query]);

  useEffect(() => {
    const trimmed = (debouncedQuery || "").trim();
    if (!enabled || !userId || !trimmed) {
      setChats([]);
      setMessages([]);
      setFetching(false);
      setError("");
      return undefined;
    }

    let cancelled = false;
    setFetching(true);
    setError("");

    const request =
      orgId != null && Number(orgId) > 0
        ? searchOrgInbox(Number(orgId), userId, trimmed)
        : searchUserInbox(userId, trimmed);

    request
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setChats(Array.isArray(res.data.chats) ? res.data.chats : []);
          setMessages(Array.isArray(res.data.messages) ? res.data.messages : []);
          setError("");
        } else {
          setChats([]);
          setMessages([]);
          setError(res.error || "Search failed");
        }
      })
      .catch(() => {
        if (cancelled) return;
        setChats([]);
        setMessages([]);
        setError("Network error searching messages");
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, enabled, orgId, userId]);

  const trimmedQuery = (query || "").trim();
  const waitingForDebounce =
    enabled && Boolean(trimmedQuery) && trimmedQuery !== (debouncedQuery || "").trim();
  const loading = fetching || waitingForDebounce;

  return { chats, messages, loading, error };
}
